// Re-checks a finished prize-wheel draw in the browser (Web Crypto), exactly
// as the API draws it (WHEEL_CONTRACT.md v1.1 "Fairness / verification"):
//
//   seed = 32 random bytes, on the wire as 64 lowercase hex; seedHash = sha256(raw seed) hex,
//   published when the wheel was scheduled. Entries get entryIndex 0..N−1 at the draw.
//   pool = [0..N−1]; for round r = 1..R (R = min(N, winnerCount)):
//     M = pool.length; for attempt a = 0, 1, 2…:
//       h = HMAC-SHA256(key = raw seed, msg = utf8 `${wheelId}:${r}:${a}`)
//       x = uint32 big-endian of h[0..4]; accept if x < 2^32 − (2^32 mod M)
//     pick = pool[x mod M] → winnerIndex_r; remove it from the pool.
//   v2: excludePreviousWinners = false → the pool stays the full entry list every round (nothing removed).

const TWO32 = 2 ** 32;

export function hexToBytes(hex) {
  if (typeof hex !== 'string' || !/^([0-9a-f]{2})+$/i.test(hex)) return null;
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(i * 2, i * 2 + 2), 16);
  return out;
}

export function bytesToHex(buf) {
  return Array.from(new Uint8Array(buf), (b) => b.toString(16).padStart(2, '0')).join('');
}

export function webCrypto() {
  const c = typeof window !== 'undefined' ? window.crypto : undefined;
  return c && c.subtle ? c.subtle : null;
}

/** The winner indices the seed gives, round by round. */
export async function drawWinnerIndices({ seed, wheelId, participantCount, winnerCount, excludePreviousWinners = true }, subtle = webCrypto()) {
  const key = hexToBytes(seed);
  if (!subtle || !key) throw new Error('unavailable');
  const N = Math.max(0, Math.floor(participantCount));
  const K = Math.max(0, Math.floor(winnerCount));
  const R = N === 0 ? 0 : excludePreviousWinners ? Math.min(N, K) : K;
  const hmacKey = await subtle.importKey('raw', key, { name: 'HMAC', hash: 'SHA-256' }, false, ['sign']);
  const enc = new TextEncoder();
  const pool = Array.from({ length: N }, (_, i) => i);
  const picks = [];
  for (let r = 1; r <= R; r++) {
    const M = pool.length;
    const limit = TWO32 - (TWO32 % M);
    for (let a = 0; a < 1000; a++) {
      const h = new DataView(await subtle.sign('HMAC', hmacKey, enc.encode(`${wheelId}:${r}:${a}`)));
      const x = h.getUint32(0, false);
      if (x < limit) {
        picks.push(excludePreviousWinners ? pool.splice(x % M, 1)[0] : pool[x % M]);
        break;
      }
    }
  }
  return picks;
}

/**
 * → { hashOk, winnersOk, expected: number[] } — hashOk: sha256(seed) = seedHash;
 * winnersOk: the recomputed indices are the winners' entryIndex, round by round
 * (null when the winners carry no entryIndex to compare).
 */
export async function verifyDraw({ wheelId, seed, seedHash, participantCount, winnerCount, winners, excludePreviousWinners = true }, subtle = webCrypto()) {
  const raw = hexToBytes(seed);
  if (!subtle || !raw) throw new Error('unavailable');
  const hashOk = bytesToHex(await subtle.digest('SHA-256', raw)) === String(seedHash || '').toLowerCase();
  const expected = await drawWinnerIndices({ seed, wheelId, participantCount, winnerCount, excludePreviousWinners }, subtle);
  const got = (winners || []).slice().sort((a, b) => (a.round ?? 0) - (b.round ?? 0)).map((w) => w.entryIndex);
  const winnersOk = got.some((x) => x === null || x === undefined)
    ? null
    : got.length === expected.length && got.every((x, i) => x === expected[i]);
  return { hashOk, winnersOk, expected };
}
